import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { VERTICAL_LINKS } from '../data/verticalLinks';

const navClass = ({ isActive }) =>
  `text-sm font-medium transition-colors ${
    isActive ? 'text-primary-700' : 'text-slate-600 hover:text-slate-900'
  }`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSectorsOpen, setIsSectorsOpen] = useState(false);
  const [isMobileSectorsOpen, setIsMobileSectorsOpen] = useState(false);
  const industriesRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = e => {
      if (industriesRef.current && !industriesRef.current.contains(e.target)) {
        setIsSectorsOpen(false);
      }
    };
    const handleKeyDown = e => {
      if (e.key === 'Escape') setIsSectorsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const closeMenu = () => {
    setIsOpen(false);
    setIsMobileSectorsOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow ${
        isScrolled
          ? 'border-b border-slate-200/80 bg-white/95 shadow-soft backdrop-blur-md'
          : 'border-b border-transparent bg-white/90 backdrop-blur-sm'
      }`}
    >
      <div className="container-custom">
        <div className="flex h-16 items-center justify-between gap-4 lg:h-[4.25rem]">
          <Link to="/" aria-label="IDGate home" className="shrink-0">
            <img src="/logos/logo.png" alt="IDGate" className="h-48 w-auto" />
          </Link>

          <nav className="hidden items-center gap-6 md:flex" aria-label="Primary">
            <NavLink to="/" className={navClass}>Home</NavLink>
            <a href="/#how" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">How it works</a>
            <a href="/#for-operators" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">For your team</a>

            {/* Sectors dropdown */}
            <div className="relative" ref={industriesRef}>
              <button
                type="button"
                onClick={() => setIsSectorsOpen(v => !v)}
                aria-expanded={isSectorsOpen}
                aria-haspopup="true"
                className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                Sectors
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-200 ${isSectorsOpen ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {isSectorsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute left-1/2 top-full mt-3 w-[480px] -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-4 shadow-xl"
                  >
                    <a
                      href="/#verticals"
                      onClick={() => setIsSectorsOpen(false)}
                      className="mb-2 flex items-center gap-1.5 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                    >
                      All sectors in one picture &#8250;
                    </a>
                    <div className="grid grid-cols-3 gap-0.5">
                      {VERTICAL_LINKS.map(v => (
                        <Link
                          key={v.path}
                          to={v.path}
                          onClick={() => setIsSectorsOpen(false)}
                          className="rounded-lg px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                        >
                          {v.label}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a href="/#integrate" className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">Integrate</a>
            <NavLink to="/contact" className={navClass}>Contact us</NavLink>
            <NavLink
              to="/demo"
              className="rounded-md border border-primary-600 px-3.5 py-2 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
            >
              Try demo
            </NavLink>
            <a
              href="https://app.idgate.in/login"
              rel="noopener noreferrer"
              className="btn-secondary py-2.5 text-sm"
            >
              Login
            </a>
            <a
              href="https://app.idgate.in/signup"
              rel="noopener noreferrer"
              className="btn-primary py-2.5 text-sm"
            >
              Sign up
            </a>
          </nav>

          <button
            type="button"
            className="rounded-md p-2 text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-primary-500 md:hidden"
            onClick={() => setIsOpen(!isOpen)}
            aria-expanded={isOpen}
            aria-controls="mobile-nav"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-slate-200 bg-white md:hidden"
          >
            <div className="container-custom flex flex-col gap-1 py-4">
              <NavLink to="/" onClick={closeMenu} className="py-3 text-base font-medium text-slate-800">Home</NavLink>
              <a href="/#how" onClick={closeMenu} className="py-3 text-base font-medium text-slate-800">How it works</a>
              <a href="/#for-operators" onClick={closeMenu} className="py-3 text-base font-medium text-slate-800">For your team</a>

              {/* Mobile industries expandable */}
              <div>
                <button
                  type="button"
                  onClick={() => setIsMobileSectorsOpen(v => !v)}
                  className="flex w-full items-center justify-between py-3 text-base font-medium text-slate-800"
                >
                  Sectors
                  <ChevronDown
                    size={16}
                    className={`transition-transform duration-200 ${isMobileSectorsOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isMobileSectorsOpen && (
                  <div className="mb-2 ml-2 border-l-2 border-slate-100 pl-3">
                    <a
                      href="/#verticals"
                      onClick={closeMenu}
                      className="mb-2 block py-1.5 text-sm font-semibold text-primary-700"
                    >
                      All sectors in one picture &#8250;
                    </a>
                    <div className="grid grid-cols-2 gap-x-4">
                      {VERTICAL_LINKS.map(v => (
                        <Link
                          key={v.path}
                          to={v.path}
                          onClick={closeMenu}
                          className="py-1.5 text-sm text-slate-600 hover:text-primary-700"
                        >
                          {v.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a href="/#integrate" onClick={closeMenu} className="py-3 text-base font-medium text-slate-800">Integrate</a>
              <NavLink to="/contact" onClick={closeMenu} className="py-3 text-base font-medium text-slate-800">
                Contact us
              </NavLink>
              <NavLink
                to="/demo"
                onClick={closeMenu}
                className="py-3 text-base font-semibold text-primary-700"
              >
                Try demo
              </NavLink>
              <a
                href="https://app.idgate.in/login"
                rel="noopener noreferrer"
                className="py-3 text-base font-medium text-slate-800"
              >
                Login
              </a>
              <a
                href="https://app.idgate.in/signup"
                rel="noopener noreferrer"
                className="btn-primary mt-2 w-full text-center"
              >
                Sign up
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
